/* ============================================================================
   Lab Grown Diamond Jewelry hub — SECTION BEHAVIOUR
   ----------------------------------------------------------------------------
   The hub is the one page that does not have a single grid. It has one grid per
   jewelry family, each with its own View More, so a shopper can see what the
   shelf actually holds instead of scrolling 978 mixed products.

   The shared collection shell (_ds/collection/collection.js) still runs on this
   page and still owns Favorite Picks, the reviews marquee, the FAQ accordion and
   the scroll reveals. It leaves the product grid alone because there is no
   #cg-grid here (it returns early), so this file adds behaviour rather than
   replacing any.

   Globals this file expects, all set before it runs:
     window.PS_COLLECTION  — assets/data/products.js (the 978-piece snapshot)
     window.PS_LABHUB      — assets/data/families.js (tools/build_lab_hub_data.py)
     window.PS_PAGE.hub    — card labels + section sizing

   Cards carry no compare or wishlist button: both are wired by the shell's grid
   pipeline, which does not run here. The filterable all-978 view keeps them and
   is linked from the toolbar row.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.PS_PAGE || {};
  var HUB = window.PS_LABHUB || { families: [] };
  var DATA = window.PS_COLLECTION || { products: [] };
  var HUBCFG = CFG.hub || {};

  var PORT = window.PS_PORT || null;
  var DS = CFG.ds || '../_ds';
  function dsAsset(name) { return PORT ? PORT.base + 'ps-26-' + name : DS + '/assets/' + name; }

  var CARD_LABEL = HUBCFG.cardLabels || {};
  var ROWS = HUBCFG.rows || 2;                 /* rows revealed per View More click */
  var SHAPE_LABEL = {round:'Round', princess:'Princess', emerald:'Emerald', oval:'Oval', cushion:'Cushion', marquise:'Marquise', pear:'Pear', asscher:'Asscher', radiant:'Radiant', heart:'Heart'};
  var CARD_SHAPES = CFG.shapeIcons || ['round', 'princess', 'emerald', 'oval', 'cushion'];
  var ORIGIN_LABEL = 'Lab-Grown Diamond';
  /* 626 of the 978 pieces are render-backed: three metals, two angles, hosted on Drive.
     Platinum has no render of its own and never will — it reads as white gold in a
     photograph — so it points at the white images, exactly as the shell does. */
  var METALS = [
    {key:'white',    src:'white', code:'WG', label:'14K White Gold',  fill:'var(--ps-metal-white)',  ink:'var(--ps-navy)'},
    {key:'yellow',   src:'yellow',code:'YG', label:'14K Yellow Gold', fill:'var(--ps-metal-yellow)', ink:'var(--ps-bg)'},
    {key:'rose',     src:'rose',  code:'RG', label:'14K Rose Gold',   fill:'var(--ps-metal-rose)',   ink:'var(--ps-bg)'},
    {key:'platinum', src:'white', code:'PT', label:'Platinum',        fill:'var(--ps-metal-plat)',   ink:'var(--ps-navy)'}
  ];
  var BLANK_IMG = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

  var BY_ID = {};
  for (var i = 0; i < DATA.products.length; i++) BY_ID[DATA.products[i].id] = DATA.products[i];

  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  /* PSMoney (layout/ps26): the shopper's currency, never a hard-coded '$' (owner 2026-10-05). */
  function money(n) { return window.PSMoney ? PSMoney.fmt(n, 2) : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  /* Not anchored to the start: some titles lead with an adjective and were losing
     their carat weight entirely (the same fix the shell carries). */
  function parseCarat(t) { var m = t.match(/([\d.]+(?:-[\d.]+)?)\s*CT\b/i); return m ? m[1].replace('-', '–') : ''; }

  /* A card is named from the section it sits in, not by matching the title against a
     family table. The section already IS the family, so "Round Cut Bridal Set" needs
     no guessing, and a title that names two shapes keeps both. */
  function cardName(p, label) {
    var m = /CT\s+(.+?)\s+Cut\b/i.exec(p.title);
    var shape = m ? m[1] : (SHAPE_LABEL[p.shape] || '');
    if (!shape) return label;
    var head = shape.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (new RegExp('^' + head + '\\b', 'i').test(label)) return label;
    return shape + ' ' + label;
  }
  function specLine(p) {
    var carat = parseCarat(p.title);
    return carat ? carat + ' CT · Lab Grown Diamonds' : 'Lab Grown Diamonds';
  }
  function starsHTML(r) {
    var full = Math.round(r), out = '';
    for (var i = 0; i < 5; i++) out += '<svg viewBox="0 0 24 24"' + (i < full ? '' : ' style="opacity:.28"') + '><path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z"/></svg>';
    return out;
  }
  function priceHTML(p) {
    var off = (p.priceWas && p.priceWas > p.priceNow && p.pctOff) ? '<span class="off">−' + p.pctOff + '%</span>' : '';
    return '<span class="price"><span class="now">' + money(p.priceNow) + '</span>' +
      (p.priceWas ? '<span class="was">' + money(p.priceWas) + '</span>' : '') + off + '</span>';
  }
  function ratingHTML(p) {
    return (p.rating != null)
      ? '<span class="pcard-rating"><span class="stars">' + starsHTML(p.rating) + '</span>' + p.rating.toFixed(1) + '</span>'
      : '';
  }
  /* A shelf of one metal reads as one product repeated, so each card opens on a metal
     drawn 60% white / 20% yellow / 20% rose. Derived from the product's rank, like the
     shell's, so the mix is identical on every reload and review screenshots compare. */
  function defaultMetal(p) {
    if (p.images.source !== 'render-drive') return 'white';
    var r = (p.rank * 2654435761 % 4294967296) % 100;
    var metal = r < 60 ? 'white' : (r < 80 ? 'yellow' : 'rose');
    return p.images.byColor[metal] ? metal : 'white';
  }
  function cardHTML(p, label) {
    var name = cardName(p, label);
    var isRender = p.images.source === 'render-drive';
    var hasMetals = !!p.images.byColor;
    var metal0 = defaultMetal(p);
    /* byColor holds {a,b,angles} objects on render-backed pieces and plain CDN URL
       strings on the rest. Both mean "this piece exists in this metal", so both are
       read here — reading only the object form is what made a colour click on a
       CDN-backed card assign "undefined". */
    var cdnColor = (hasMetals && !isRender) ? (p.images.byColor[metal0] || p.images.single) : null;
    var frontSrc = isRender ? p.images.byColor[metal0].a : (cdnColor || p.images.single || p.images.cdn);
    var backSrc = isRender ? p.images.byColor[metal0].b : '';
    var shapes = '<div class="sdots" aria-label="Center stone shape: ' + (SHAPE_LABEL[p.shape] || 'not specified') + '">' +
      CARD_SHAPES.map(function (s) {
        return '<span class="sdot' + (s === p.shape ? ' is-sel' : '') + '" title="' + SHAPE_LABEL[s] + '">' +
          '<img src="' + dsAsset(s + '.png') + '" alt="" width="14" height="14" loading="lazy"></span>';
      }).join('') + '</div>';
    var metals = hasMetals ? '<div class="mdots">' + METALS.map(function (m) {
      return '<button type="button" class="mdot' + (m.key === metal0 ? ' is-sel' : '') + '" data-color="' + m.src + '"' +
        ' style="--m:' + m.fill + ';--mtext:' + m.ink + '" title="' + m.label + '" aria-label="' + m.label + '">' +
        '<span class="code">' + m.code + '</span></button>';
    }).join('') + '</div>' : '';
    var imgsAttr = hasMetals ? ' data-imgs="' + esc(JSON.stringify(p.images.byColor)) + '"' : '';
    var cdnAttr = isRender && p.images.cdn ? ' data-cdn="' + esc(p.images.cdn) + '"' : '';
    return '<article class="pcard pcard--grid" data-id="' + p.id + '"' + imgsAttr + '>' +
      '<a class="pimg" href="' + p.url + '">' +
        (isRender
          ? '<img class="pi-front" src="' + BLANK_IMG + '" data-dsrc="' + frontSrc + '"' + cdnAttr + ' alt="' + esc(name) + '">'
          : '<img class="pi-front" src="' + frontSrc + '" alt="' + esc(name) + '" loading="lazy">') +
        (backSrc ? '<img class="pi-back" src="' + BLANK_IMG + '" data-dsrc="' + backSrc + '"' + cdnAttr + ' alt="" aria-hidden="true">' : '') +
        '<span class="pcard-origin">' + ORIGIN_LABEL + '</span>' +
      '</a>' +
      '<a class="pmeta" href="' + p.url + '">' +
        '<span class="pname">' + esc(name) + '</span>' +
        '<span class="pspec">' + specLine(p) + '</span>' +
        ratingHTML(p) + priceHTML(p) +
      '</a>' +
      shapes +
      metals +
    '</article>';
  }

  /* ---- Drive image loader ----------------------------------------------------
     Copied from _ds/collection/collection.js:333-369 rather than shared, because the
     shell's copy is inside its own closure and returns before this page needs it. lh3
     refuses burst hotlinking, so images queue through a small concurrency window and
     each falls back lh3 -> Drive thumbnail -> the Shopify CDN copy before giving up. */
  var DRIVE_Q = [], DRIVE_ACTIVE = 0, DRIVE_MAX = 5;
  function driveFileId(u) { var m = /\/d\/([^=?&]+)/.exec(u || ''); return m ? m[1] : null; }
  function pumpDrive() {
    while (DRIVE_ACTIVE < DRIVE_MAX && DRIVE_Q.length) {
      var img = DRIVE_Q.shift();
      if (img.isConnected && img.getAttribute('data-dsrc')) loadDrive(img, 0);
    }
  }
  function loadDrive(img, attempt) {
    var base = img.getAttribute('data-dsrc');
    var id = driveFileId(base);
    var url = attempt === 0 ? base + '=w900'
            : (attempt === 1 && id) ? 'https://drive.google.com/thumbnail?id=' + id + '&sz=w900'
            : img.getAttribute('data-cdn') || '';
    if (!url) { pumpDrive(); return; }
    DRIVE_ACTIVE++;
    img.onload = function () { DRIVE_ACTIVE--; img.onload = img.onerror = null; img.removeAttribute('data-dsrc'); pumpDrive(); };
    img.onerror = function () {
      DRIVE_ACTIVE--; img.onload = img.onerror = null;
      if (attempt < 2) setTimeout(function () { loadDrive(img, attempt + 1); }, 600 * (attempt + 1));
      pumpDrive();
    };
    img.src = url;
  }
  var driveIO = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (en.isIntersecting) { driveIO.unobserve(en.target); DRIVE_Q.push(en.target); pumpDrive(); }
    });
  }, { rootMargin: '500px' }) : null;
  function hydrateDriveImgs(scope) {
    scope.querySelectorAll('img[data-dsrc]').forEach(function (img) {
      if (driveIO) driveIO.observe(img); else { DRIVE_Q.push(img); pumpDrive(); }
    });
  }

  /* How many columns the grid is actually showing. Read from the resolved template
     so one number drives both the first paint and every View More click, and the
     revealed block is always whole rows. `repeat(4, 1fr)` can survive unresolved
     before layout, so it is parsed rather than trusted to be a pixel list. */
  function gridCols(el) {
    var tpl = getComputedStyle(el).gridTemplateColumns || '';
    var rep = /repeat\(\s*(\d+)/.exec(tpl);
    if (rep) return parseInt(rep[1], 10);
    var n = tpl.split(' ').filter(function (s) { return s && s !== 'none'; }).length;
    return n || 4;
  }

  function initSection(sec, fam) {
    var grid = sec.querySelector('.hubrow-grid');
    var btn = sec.querySelector('.hubrow-more');
    var meta = sec.querySelector('.hubrow-meta');
    var bar = sec.querySelector('.hubrow-bar');
    if (!grid) return;

    var ids = fam.ids || [];
    var label = CARD_LABEL[fam.handle] || fam.label;
    var shown = 0;

    function reveal() {
      if (shown >= ids.length) return;
      var chunk = gridCols(grid) * ROWS;
      var slice = ids.slice(shown, shown + chunk);
      grid.insertAdjacentHTML('beforeend', slice.map(function (id) {
        var p = BY_ID[id];
        return p ? cardHTML(p, label) : '';
      }).join(''));
      shown += slice.length;
      hydrateDriveImgs(grid);
      update();
    }
    function update() {
      if (meta) meta.textContent = 'Showing ' + shown + ' of ' + ids.length;
      if (bar) bar.style.transform = 'scaleX(' + (ids.length ? shown / ids.length : 0) + ')';
      if (btn) btn.hidden = shown >= ids.length;
    }

    if (btn) btn.addEventListener('click', reveal);

    /* Metal swap. Every URL is already on the card in data-imgs, so a click assigns
       img.src and nothing is fetched twice; the back image follows the front so the
       hover still shows the same piece in the metal the shopper chose. */
    grid.addEventListener('click', function (e) {
      var md = e.target.closest('.mdot');
      if (!md) return;
      var card = md.closest('.pcard--grid');
      var imgs = JSON.parse(card.getAttribute('data-imgs') || '{}');
      var picked = imgs[md.getAttribute('data-color')];
      if (!picked) return;
      e.preventDefault();
      /* A Drive render is {a,b} and wants the =w900 size suffix; a CDN variant photo is
         already a whole URL and must be used as it stands. */
      var isDrive = typeof picked === 'object';
      var front = card.querySelector('.pi-front'), back = card.querySelector('.pi-back');
      if (front) { front.removeAttribute('data-dsrc'); front.src = isDrive ? picked.a + '=w900' : picked; }
      if (back && isDrive && picked.b) { back.removeAttribute('data-dsrc'); back.src = picked.b + '=w900'; }
      card.querySelectorAll('.mdot').forEach(function (d) { d.classList.toggle('is-sel', d === md); });
    });

    reveal();
  }

  var sections = document.querySelectorAll('.hubrow[data-family]');
  var byHandle = {};
  HUB.families.forEach(function (f) { byHandle[f.handle] = f; });

  for (var s = 0; s < sections.length; s++) {
    var handle = sections[s].getAttribute('data-family');
    var fam = byHandle[handle];
    if (!fam) { console.warn('[labhub] no data for section ' + handle); continue; }
    initSection(sections[s], fam);
  }

  /* The markup carries the section order so the H2s are crawlable; the data carries
     the sales ranking. If a rebuild reorders the families, say so out loud instead of
     letting the page quietly disagree with what sells. */
  var domOrder = [].map.call(sections, function (el) { return el.getAttribute('data-family'); }).join(',');
  var dataOrder = HUB.families.map(function (f) { return f.handle; }).join(',');
  if (domOrder !== dataOrder) {
    console.warn('[labhub] section order differs from the sales ranking\n  page: ' + domOrder + '\n  data: ' + dataOrder);
  }
})();
